import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameMonth, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useAppStore } from '../store';
import { Button } from '../components/ui/Button';

export const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const tasks = useAppStore((state) => state.tasks);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));

  const getTasksForDate = (date: Date) => {
    return tasks.filter(task => {
      const taskDate = new Date(task.dueDate);
      return (
        taskDate.getDate() === date.getDate() &&
        taskDate.getMonth() === date.getMonth() &&
        taskDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const priorityColors = {
    High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    Medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    Low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Calendar</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Schedule and manage your tasks
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />}>
          New Task
        </Button>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevMonth}
            leftIcon={<ChevronLeft className="w-4 h-4" />}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={nextMonth}
            rightIcon={<ChevronRight className="w-4 h-4" />}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Days of Week */}
        <div className="grid grid-cols-7 gap-px border-b dark:border-gray-700">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="py-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-px">
          {daysInMonth.map((date, idx) => {
            const dayTasks = getTasksForDate(date);
            const isSelected = selectedDate && 
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth();

            return (
              <div
                key={idx}
                onClick={() => setSelectedDate(date)}
                className={`min-h-[120px] p-2 ${
                  !isSameMonth(date, currentDate)
                    ? 'bg-gray-50 dark:bg-gray-900'
                    : isSelected
                    ? 'bg-blue-50 dark:bg-blue-900'
                    : 'bg-white dark:bg-gray-800'
                } ${
                  isToday(date)
                    ? 'border-2 border-[#4b39ef]'
                    : ''
                } cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${
                      !isSameMonth(date, currentDate)
                        ? 'text-gray-400 dark:text-gray-600'
                        : isToday(date)
                        ? 'text-[#4b39ef] font-bold'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {format(date, 'd')}
                  </span>
                  {dayTasks.length > 0 && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-gradient-to-r from-[#4b39ef] to-[#39d2c0] rounded-full">
                      {dayTasks.length}
                    </span>
                  )}
                </div>

                {/* Tasks for the day */}
                <div className="mt-2 space-y-1">
                  {dayTasks.slice(0, 2).map((task, taskIdx) => (
                    <div
                      key={taskIdx}
                      className={`px-2 py-1 text-xs rounded-md ${priorityColors[task.priority]}`}
                    >
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 2 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 pl-2">
                      +{dayTasks.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Tasks */}
      {selectedDate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Tasks for {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          <div className="space-y-4">
            {getTasksForDate(selectedDate).map((task, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 rounded-lg border dark:border-gray-700"
              >
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {task.title}
                  </h4>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {task.description}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-md ${
                    priorityColors[task.priority]
                  }`}
                >
                  {task.priority}
                </span>
              </div>
            ))}
            {getTasksForDate(selectedDate).length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400">
                No tasks scheduled for this day
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};